import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    Box,
    Drawer,
    List,
    ListItem,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Divider,
    Avatar,
    Badge
} from '@mui/material';
import {
    Dashboard as DashboardIcon,
    Report as ReportIcon,
    Person as UserIcon,
    Analytics as AnalyticsIcon,
    ExitToApp as ExitIcon,
    AdminPanelSettings as AdminIcon,
    Speed as MonitoringIcon,
    VerifiedUser as VerifiedUserIcon,
    Videocam as VideoIcon,
    PhotoLibrary as PhotoIcon
} from '@mui/icons-material';
import { useAppSelector, useAppDispatch } from '../../../store/hooks';
import { clearAuthState } from '../../../store/slices/authSlice';

const DRAWER_WIDTH = 260;

interface AdminSidebarProps {
    pendingReportsCount?: number;
    pendingVideosCount?: number;
}

/**
 * AdminSidebar component for admin dashboard navigation
 * 
 * Features:
 * - Clean navigation menu with icons
 * - Badge indicators for pending reports
 * - Admin user profile display
 * - Logout functionality
 * - Active route highlighting
 * - Responsive design
 */
const AdminSidebar: React.FC<AdminSidebarProps> = ({
    pendingReportsCount = 0,
    pendingVideosCount = 0,
}) => {
    const navigate = useNavigate();
    const location = useLocation();
    const dispatch = useAppDispatch();
    const { user } = useAppSelector((state) => state.auth);

    const menuItems = [
        {
            text: 'Dashboard',
            icon: <DashboardIcon />,
            path: '/admin',
            exact: true
        },
        {
            text: 'Reports',
            icon: (
                <Badge badgeContent={pendingReportsCount} color="error" max={99}>
                    <ReportIcon />
                </Badge>
            ),
            path: '/admin/reports',
        },
        {
            text: 'User Management',
            icon: <UserIcon />,
            path: '/admin/users',
        },
        {
            text: 'Analytics',
            icon: <AnalyticsIcon />,
            path: '/admin/analytics',
        },
        {
            text: 'Verification Review',
            icon: <VerifiedUserIcon />,
            path: '/admin/verification',
        },
        {
            text: 'Video Review',
            icon: (
                <Badge badgeContent={pendingVideosCount} color="error" max={99}>
                    <VideoIcon />
                </Badge>
            ),
            path: '/admin/videos',
        },
        {
            text: 'Photo Review',
            icon: <PhotoIcon />,
            path: '/admin/photos',
        },
        {
            text: 'System Monitoring',
            icon: <MonitoringIcon />,
            path: '/admin/monitoring',
        },
    ];

    const isActive = (path: string, exact = false) => {
        if (exact) {
            return location.pathname === path;
        }
        return location.pathname.startsWith(path);
    };

    const handleNavigation = (path: string) => {
        navigate(path);
    };

    const handleLogout = () => {
        dispatch(clearAuthState());
        navigate('/login');
    };

    const getInitials = (email: string) => {
        return email.charAt(0).toUpperCase();
    };

    return (
        <Drawer
            variant="permanent"
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    backgroundColor: '#1a1a2e',
                    color: 'white',
                },
            }}
        >
            {/* Header */}
            <Box sx={{ p: 3, textAlign: 'center', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <AdminIcon sx={{ color: '#A76FFF', fontSize: 32 }} />
                    <Typography variant="h6" fontWeight="bold" color="white">
                        Admin Panel
                    </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                    Pop the Balloon
                </Typography>
            </Box>

            {/* Admin Profile */}
            <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Avatar 
                        sx={{ 
                            bgcolor: '#A76FFF',
                            width: 40,
                            height: 40,
                            fontSize: '14px'
                        }}
                    >
                        {user?.email ? getInitials(user.email) : 'A'}
                    </Avatar>
                    <Box sx={{ flex: 1, overflow: 'hidden' }}>
                        <Typography 
                            variant="body2" 
                            fontWeight="medium"
                            sx={{ 
                                color: 'white',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                            }}
                        >
                            {user?.email || 'Admin User'}
                        </Typography>
                        <Typography 
                            variant="caption" 
                            sx={{ color: 'rgba(255,255,255,0.6)' }}
                        >
                            Administrator
                        </Typography>
                    </Box>
                </Box>
            </Box>

            {/* Navigation Menu */}
            <List sx={{ flex: 1, py: 2 }}>
                {menuItems.map((item) => (
                    <ListItem key={item.text} disablePadding sx={{ mb: 0.5 }}>
                        <ListItemButton
                            onClick={() => handleNavigation(item.path)}
                            sx={{
                                mx: 1,
                                borderRadius: 2,
                                backgroundColor: isActive(item.path, item.exact) 
                                    ? 'rgba(167, 111, 255, 0.2)' 
                                    : 'transparent',
                                '&:hover': {
                                    backgroundColor: 'rgba(167, 111, 255, 0.1)',
                                },
                                '& .MuiListItemIcon-root': {
                                    color: isActive(item.path, item.exact) ? '#A76FFF' : 'rgba(255,255,255,0.7)',
                                },
                                '& .MuiListItemText-primary': {
                                    color: isActive(item.path, item.exact) ? 'white' : 'rgba(255,255,255,0.8)',
                                    fontWeight: isActive(item.path, item.exact) ? 600 : 400,
                                },
                            }}
                        >
                            <ListItemIcon sx={{ minWidth: 40 }}>
                                {item.icon}
                            </ListItemIcon>
                            <ListItemText 
                                primary={item.text}
                                primaryTypographyProps={{ 
                                    fontSize: '14px'
                                }}
                            />
                        </ListItemButton>
                    </ListItem>
                ))}
            </List>

            <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)' }} />

            {/* Bottom Actions */}
            <List sx={{ py: 2 }}>
                <ListItem disablePadding>
                    <ListItemButton
                        onClick={handleLogout}
                        sx={{
                            mx: 1,
                            borderRadius: 2,
                            '&:hover': {
                                backgroundColor: 'rgba(244, 67, 54, 0.1)',
                            },
                        }}
                    >
                        <ListItemIcon sx={{ minWidth: 40 }}>
                            <ExitIcon sx={{ color: 'rgba(244, 67, 54, 0.8)' }} />
                        </ListItemIcon>
                        <ListItemText 
                            primary="Logout"
                            primaryTypographyProps={{ 
                                fontSize: '14px',
                                color: 'rgba(244, 67, 54, 0.8)'
                            }}
                        />
                    </ListItemButton>
                </ListItem>
            </List>
        </Drawer>
    );
};

export default AdminSidebar;